import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import FlightDetails from "./FlightResults/FlightDetails";
import {
  FaPlane,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaChair,
  FaSuitcase,
  FaExchangeAlt,
  FaCheckCircle,
  FaSpinner,
  FaInfoCircle,
  FaUtensils,
} from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { formatPriceSync } from "../utils/currency";

const RoundTripFlightCard = ({
  flight,
  index,
  formatApiDateTime,
  minutesToHm,
  verifyFlightPrice,
  handleAuthCheck,
  statusRefdId,
}) => {
  const { returnRefId, setOnwardRefID } = useAuth();
  const { currency } = useCurrency();

  // Debug log

  const onwardFlight = flight?.onwardFlight || flight?.onward || {};
  const returnFlight = flight?.returnFlight || flight?.return || {};

  // State management
  const [onwardFareOptions, setOnwardFareOptions] = useState([]);
  const [returnFareOptions, setReturnFareOptions] = useState([]);
  const [loadingOnwardFares, setLoadingOnwardFares] = useState(false);
  const [loadingReturnFares, setLoadingReturnFares] = useState(false);
  const [selectedOnwardFare, setSelectedOnwardFare] = useState(null);
  const [selectedReturnFare, setSelectedReturnFare] = useState(null);
  const [showOnwardFares, setShowOnwardFares] = useState(false);
  const [showReturnFares, setShowReturnFares] = useState(false);
  const [expandedOnwardDetails, setExpandedOnwardDetails] = useState(false);
  const [expandedReturnDetails, setExpandedReturnDetails] = useState(false);

  // Helper functions
  const getSegments = (leg) => {
    if (!leg) return [];

    // Try different possible data structures
    if (Array.isArray(leg)) {
      return leg.filter((s) => s && s.flightNo);
    }

    if (leg?.Flights?.Onward) {
      if (Array.isArray(leg.Flights.Onward)) {
        return leg.Flights.Onward.filter((s) => s && s.flightNo);
      }
      // If it's an object, convert to array
      return Object.values(leg.Flights.Onward).filter((s) => s && s.flightNo);
    }

    if (typeof leg === "object") {
      const values = Object.values(leg);
      return values.filter((s) => s && s.flightNo);
    }

    return [];
  };

  const getStopsText = (leg) => {
    const segs = getSegments(leg);
    const stops = Math.max(0, segs.length - 1);
    if (stops === 0) return "Non-stop";
    const vias = segs
      .slice(0, -1)
      .map((s) => s.arrCode)
      .filter(Boolean)
      .join(", ");
    return `${stops} Stop${stops > 1 ? "s" : ""}${vias ? ` via ${vias}` : ""}`;
  };

  const getTotalDurationMinutes = (leg) => {
    if (!leg) return 0;

    // Try different duration properties
    if (leg.durTotal) return Number(leg.durTotal);
    if (leg?.Fares?.[0]?.duration) return Number(leg.Fares[0].duration);

    const segs = getSegments(leg);
    return segs.reduce((sum, s) => sum + Number(s.duration || s.dur || 0), 0);
  };

  const onwardSegments = getSegments(onwardFlight);
  const returnSegments = getSegments(returnFlight);

  const onwardFirstSegment = onwardSegments[0] || {};
  const onwardLastSegment = onwardSegments[onwardSegments.length - 1] || {};
  const returnFirstSegment = returnSegments[0] || {};
  
  // V2: Extract enhanced fare information
  const onwardFare = onwardFlight?.Fare || onwardFlight?.fare || {};
  const returnFare = returnFlight?.Fare || returnFlight?.fare || {};
  
  // V2: Get fare type information
  const getFareTypeBadge = (fare) => {
    const fareTypeDesc = fare?.fareTypeDesc?.toLowerCase() || "";
    if (fareTypeDesc.includes("student")) return { text: "Student", color: "bg-green-100 text-green-800" };
    if (fareTypeDesc.includes("senior")) return { text: "Senior Citizen", color: "bg-purple-100 text-purple-800" };
    if (fareTypeDesc.includes("defence")) return { text: "Defence", color: "bg-blue-100 text-blue-800" };
    if (fareTypeDesc.includes("sme")) return { text: "SME", color: "bg-yellow-100 text-yellow-800" };
    if (fareTypeDesc.includes("promo") || fareTypeDesc.includes("offer")) return { text: "Promo", color: "bg-orange-100 text-orange-800" };
    return null;
  };
  
  const onwardFareBadge = getFareTypeBadge(onwardFare);
  const returnFareBadge = getFareTypeBadge(returnFare);
  
  // V2: Terminal and operating airline info
  const onwardDepTerminal = onwardFirstSegment?.depTer || "";
  const onwardArrTerminal = onwardLastSegment?.arrTer || "";
  const returnDepTerminal = returnFirstSegment?.depTer || "";
  const returnArrTerminal = returnSegments[returnSegments.length - 1]?.arrTer || "";
  
  const onwardOperatingAirline = onwardFirstSegment?.airNameOp;
  const returnOperatingAirline = returnFirstSegment?.airNameOp;
  const returnLastSegment = returnSegments[returnSegments.length - 1] || {};

  // Get base prices from flight data
  const baseOnwardPrice = parseInt(
    onwardFlight?.Fare?.total?.total || onwardFlight?.Fares?.[0]?.price || 9451
  );
  const baseReturnPrice = parseInt(
    returnFlight?.Fare?.total?.total || returnFlight?.Fares?.[0]?.price || 4517
  );

  // Get airline names
  const onwardAirlineName =
    onwardFlight?.Flights?.AirlineName ||
    onwardFirstSegment?.airName ||
    onwardFlight?.airline ||
    "Airline";
  const returnAirlineName =
    returnFlight?.Flights?.AirlineName ||
    returnFirstSegment?.airName ||
    returnFlight?.airline ||
    "Airline";

  const processFareOptions = (apiFares, flightData, type) => {
    if (!apiFares || !Array.isArray(apiFares)) {
      return [];
    }

    return apiFares.map((fare, index) => {
      // Extract SSR info specific to this fare
      let fareSSRInfo = null;
      let fareBaggageOptions = [];
      let fareMealOptions = [];

      if (fare.ssrInfo) {
        fareSSRInfo = fare.ssrInfo;
        // Check if SSR info is directly on the fare or in Onward property
        if (fareSSRInfo.Onward) {
          fareBaggageOptions = fareSSRInfo.Onward.Bagg || [];
          fareMealOptions = fareSSRInfo.Onward.Meal || [];
        } else if (fareSSRInfo.Bagg || fareSSRInfo.Meal) {
          // Some fares might have SSR info directly
          fareBaggageOptions = fareSSRInfo.Bagg || [];
          fareMealOptions = fareSSRInfo.Meal || [];
        }

        // Also check for baggageOption/mealOption properties (for bundled fares)
        if (fareSSRInfo.baggageOption) {
          fareBaggageOptions = [fareSSRInfo.baggageOption];
        }
        if (fareSSRInfo.mealOption) {
          fareMealOptions = [fareSSRInfo.mealOption];
        }
      }

      // Get baggage info from fare data
      const baggageInfo = fare.baggage || {};
      const flightsData = flightData?.Flights || {};
      const baggageText = baggageInfo.checkin
        ? `${baggageInfo.checkin.replace(
            "|",
            ""
          )} | ${baggageInfo.cabin.replace("|", "")} `
        : flightsData?.bagCkin?.split(" ")[0];

      return {
        id: `${type}-fare-${index}`,
        name: fare.fareName || `Fare ${index + 1}`,
        price: parseInt(
          fare.price || (type === "onward" ? baseOnwardPrice : baseReturnPrice)
        ),
        base: parseInt(fare.base || 0),
        tax: parseInt(fare.tax || 0),
        refundable: fare.refundable || "Partially Refundable*",
        baggage: baggageText,
        dateChangeFee: fare.dateChangeFee || "2,999",
        meals: fare.meals === "available" ? "Available" : "Paid",
        seats: fare.seats === "chargeable" ? "Paid" : "Free",
        color: type === "onward" ? "blue" : "green",
        type: type,
        flightData: flightData,
        ssrInfo: fareSSRInfo,
        baggageOptions: fareBaggageOptions,
        mealOptions: fareMealOptions,
        originalFare: fare,
      };
    });
  };

  const handleViewOnwardPrices = async () => {
    if (showOnwardFares) {
      setShowOnwardFares(false);
      return;
    }

    setLoadingOnwardFares(true);
    try {
      // Use the verifyFlightPrice API
      const flightId = onwardFirstSegment?.flightID || onwardFlight?.flightId;

      if (!flightId || !statusRefdId) {
        console.error("Missing flight ID or reference ID", {
          flightId,
          statusRefdId,
        });
        throw new Error("Missing flight ID or reference ID");
      }

      setOnwardRefID(statusRefdId);

      const fareOptions = await verifyFlightPrice(
        flightId,
        statusRefdId,
        false
      );

      // Process the API response
      const processedFares = processFareOptions(
        fareOptions,
        onwardFlight,
        "onward"
      );

      if (processedFares.length === 0) {
        processedFares.push({
          id: "onward-default",
          name: "Standard Fare",
          price: baseOnwardPrice,
          refundable: "Partially Refundable*",
          baggage: onwardFlight?.Flights?.bagCkin?.split(" ")[0],
          dateChangeFee: "Contact Airlines",
          meals: "Paid",
          seats: "Paid",
          color: "blue",
          type: "onward",
          flightData: onwardFlight,
          ssrInfo: null,
          baggageOptions: [],
          mealOptions: [],
        });
      }

      setOnwardFareOptions(processedFares);
      setShowOnwardFares(true);
    } catch (error) {
      console.error("Error fetching onward fare options:", error);
      const defaultFares = [
        {
          id: "onward-default",
          name: "Standard Fare",
          price: baseOnwardPrice,
          refundable: "Partially Refundable*",
          baggage: onwardFlight?.Flights?.bagCkin?.split(" ")[0],
          dateChangeFee: "Contact Airlines",
          meals: "Paid",
          seats: "Paid",
          color: "blue",
          type: "onward",
          flightData: onwardFlight,
          ssrInfo: null,
          baggageOptions: [],
          mealOptions: [],
        },
      ];

      setOnwardFareOptions(defaultFares);
      setShowOnwardFares(true);
    } finally {
      setLoadingOnwardFares(false);
    }
  };

  const handleViewReturnPrices = async () => {
    if (showReturnFares) {
      setShowReturnFares(false);
      return;
    }

    setLoadingReturnFares(true);
    try {
      const flightId = returnFirstSegment?.flightID || returnFlight?.flightId;

      if (!flightId || !returnRefId) {
        console.error("Missing flight ID or reference ID", {
          flightId,
          returnRefId,
        });
        throw new Error("Missing flight ID or reference ID");
      }

      const fareOptions = await verifyFlightPrice(flightId, returnRefId, false);

      // Process the API response
      const processedFares = processFareOptions(
        fareOptions,
        returnFlight,
        "return"
      );

      if (processedFares.length === 0) {
        processedFares.push({
          id: "return-default",
          name: "Standard Fare",
          price: baseReturnPrice,
          refundable: "Partially Refundable*",
          baggage: returnFlight?.Flights?.bagCkin?.split(" ")[0],
          dateChangeFee: "Contact Airlines",
          meals: "Paid",
          seats: "Paid",
          color: "green",
          type: "return",
          flightData: returnFlight,
          ssrInfo: null,
          baggageOptions: [],
          mealOptions: [],
        });
      }

      setReturnFareOptions(processedFares);
      setShowReturnFares(true);
    } catch (error) {
      console.error("Error fetching return fare options:", error);
      const defaultFares = [
        {
          id: "return-default",
          name: "Standard Fare",
          price: baseReturnPrice,
          refundable: "Partially Refundable*",
          baggage: returnFlight?.Flights?.bagCkin?.split(" ")[0],
          dateChangeFee: "Contact Airlines",
          meals: "Paid",
          seats: "Paid",
          color: "green",
          type: "return",
          flightData: returnFlight,
          ssrInfo: null,
          baggageOptions: [],
          mealOptions: [],
        },
      ];
      setReturnFareOptions(defaultFares);
      setShowReturnFares(true);
    } finally {
      setLoadingReturnFares(false);
    }
  };

  const handleSelectOnwardFare = (fare) => {
    setSelectedOnwardFare(fare);
    setShowOnwardFares(false);
  };

  const handleSelectReturnFare = (fare) => {
    setSelectedReturnFare(fare);
    setShowReturnFares(false);
  };

  const handleBookNow = () => {
    if (!selectedOnwardFare || !selectedReturnFare) {
      alert("Please select fares for both onward and return flights");
      return;
    }

    const totalPrice = selectedOnwardFare.price + selectedReturnFare.price;

    // Prepare data for handleAuthCheck
    const bookingData = {
      isRoundTrip: true,
      onwardFlight: {
        ...onwardFlight,
        selectedFare: selectedOnwardFare,
      },
      returnFlight: {
        ...returnFlight,
        selectedFare: selectedReturnFare,
      },
      totalPrice: totalPrice,
      flightID: onwardFirstSegment?.flightID,
      refID: statusRefdId,
      selectedOnwardFare,
      selectedReturnFare,
    };

    handleAuthCheck(bookingData);
  };

  // Calculate total price based on selections
  const totalSelectedPrice =
    (selectedOnwardFare?.price || baseOnwardPrice) +
    (selectedReturnFare?.price || baseReturnPrice);

  // Format date/time safely
  const formatTime = (dateString) => {
    if (!dateString) return "--:--";

    if (formatApiDateTime) {
      const formatted = formatApiDateTime(dateString);
      return formatted?.split(",")[1]?.trim() || "--:--";
    }

    // Fallback parsing
    if (dateString.length >= 12) {
      const hour = dateString.slice(8, 10);
      const minute = dateString.slice(10, 12);
      return `${hour}:${minute}`;
    }

    return "--:--";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "--/--";

    if (formatApiDateTime) {
      const formatted = formatApiDateTime(dateString);
      return formatted?.split(",")[0] || "--/--";
    }

    // Fallback parsing
    if (dateString.length >= 8) {
      const day = dateString.slice(6, 8);
      const month = dateString.slice(4, 6);
      return `${day}/${month}`;
    }

    return "--/--";
  };

  // Get flight duration
  const onwardDuration = minutesToHm
    ? minutesToHm(getTotalDurationMinutes(onwardFlight))
    : `${Math.floor(getTotalDurationMinutes(onwardFlight) / 60)}h ${
        getTotalDurationMinutes(onwardFlight) % 60
      }m`;

  const returnDuration = minutesToHm
    ? minutesToHm(getTotalDurationMinutes(returnFlight))
    : `${Math.floor(getTotalDurationMinutes(returnFlight) / 60)}h ${
        getTotalDurationMinutes(returnFlight) % 60
      }m`;

  // Check if flight data exists
  if (
    !onwardFlight ||
    Object.keys(onwardFlight).length === 0 ||
    !returnFlight ||
    Object.keys(returnFlight).length === 0
  ) {
    console.warn("Missing flight data for round trip card");
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-6">
        <p className="text-gray-500">Flight data not available</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-6 hover:shadow-xl transition-shadow duration-300"
    >
      {/* Round Trip Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-full">
              <FaExchangeAlt className="text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Round Trip</h3>
              <p className="text-sm text-gray-600">
                {onwardAirlineName} & {returnAirlineName}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Flights Comparison */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Onward Flight */}
          <div className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="flex items-center space-x-2 mb-1">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                    Onward
                  </span>
                  <span className="text-xs text-gray-500">
                    {formatDate(onwardFirstSegment?.depDate)}
                  </span>
                </div>
                <h4 className="font-semibold text-gray-800">
                  {onwardAirlineName}
                </h4>
              </div>
              <div className="text-right">
                <span className="text-lg font-bold text-gray-900">
                  {selectedOnwardFare
                    ? `₹${selectedOnwardFare.price.toLocaleString()}`
                    : "Select fare"}
                </span>
              </div>
            </div>

            {/* Flight Route */}
            <div className="flex items-center justify-between mb-3">
              <div className="text-center">
                <div className="text-lg font-bold">
                  {onwardFirstSegment?.depCode || "---"}
                </div>
                <div className="text-sm text-gray-500">
                  {formatTime(onwardFirstSegment?.depDate)}
                </div>
              </div>

              <div className="flex-1 px-4">
                <div className="relative">
                  <div className="flex items-center">
                    <div className="flex-1 border-t border-gray-300"></div>
                    <div className="mx-2 relative">
                      <FaPlane className="text-blue-500 text-sm" />
                      <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 text-xs text-gray-500 whitespace-nowrap">
                        {onwardDuration}
                      </div>
                    </div>
                    <div className="flex-1 border-t border-gray-300"></div>
                  </div>
                  <div className="text-center text-xs text-gray-600 mt-1">
                    {getStopsText(onwardFlight)}
                  </div>
                </div>
              </div>

              <div className="text-center">
                <div className="text-lg font-bold">
                  {onwardLastSegment?.arrCode || "---"}
                </div>
                <div className="text-sm text-gray-500">
                  {formatTime(onwardLastSegment?.arrDate)}
                </div>
              </div>
            </div>

            {/* Selected Fare Info */}
            {selectedOnwardFare && (
              <div className="mb-3 p-2 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center">
                    <FaCheckCircle className="text-green-500 mr-2" />
                    <span className="font-medium">
                      {selectedOnwardFare.name}
                    </span>
                  </div>
                  <span className="text-gray-600">
                    {selectedOnwardFare.baggage}
                  </span>
                </div>

                {/* Show SSR info for selected onward fare */}
                {selectedOnwardFare.ssrInfo && (
                  <div className="mt-2 text-xs text-gray-600">
                    <div className="flex items-start">
                      <FaInfoCircle className="mr-1 text-blue-400 mt-0.5" />
                      <div>
                        {selectedOnwardFare.ssrInfo.baggageOption && (
                          <div className="mb-1">
                            • Extra Baggage:{" "}
                            {selectedOnwardFare.ssrInfo.baggageOption.baggDesc}{" "}
                            ({formatPriceSync(selectedOnwardFare.ssrInfo.baggageOption.baggAmt, currency)})
                          </div>
                        )}
                        {selectedOnwardFare.ssrInfo.mealOption && (
                          <div>
                            • In-flight Meal:{" "}
                            {selectedOnwardFare.ssrInfo.mealOption.mealDesc} (₹
                            {selectedOnwardFare.ssrInfo.mealOption.mealAmt})
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* View Prices Button */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center text-sm text-gray-600">
                <FaClock className="mr-1 text-gray-400" />
                <span>{onwardDuration}</span>
                <span className="mx-2">•</span>
                <span>{onwardFirstSegment?.flightNo || "---"}</span>
                <span className="mx-2">•</span>
                <span className="flex items-center">
                  <FaChair className="mr-1 text-gray-400" />
                  {onwardFlight?.Flights?.bagCbin}
                </span>
              </div>

              <button
                onClick={handleViewOnwardPrices}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                {loadingOnwardFares ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Loading...
                  </>
                ) : showOnwardFares ? (
                  "Hide Prices"
                ) : (
                  "View Prices"
                )}
              </button>
            </div>

            {/* Onward Flight Details Toggle */}
            <div className="border-t pt-3 mt-3">
              <button
                onClick={() => setExpandedOnwardDetails(!expandedOnwardDetails)}
                className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium w-full justify-end"
              >
                {expandedOnwardDetails ? (
                  <>
                    Hide Flight Details <FaChevronUp className="ml-1" />
                  </>
                ) : (
                  <>
                    View Flight Details <FaChevronDown className="ml-1" />
                  </>
                )}
              </button>
            </div>

            {/* Onward Fare Options */}
            <AnimatePresence>
              {showOnwardFares && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-4 border-t pt-4"
                >
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    Choose Onward Fare
                  </h4>
                  {loadingOnwardFares ? (
                    <div className="text-center py-4">
                      <FaSpinner className="animate-spin text-blue-500 text-xl mx-auto" />
                      <p className="text-gray-500 mt-2">
                        Loading fare options...
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {onwardFareOptions.length > 0 ? (
                        onwardFareOptions.map((fare, idx) => (
                          <div
                            key={fare.id || idx}
                            className={`border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                              selectedOnwardFare?.id === fare.id
                                ? "border-blue-500 bg-blue-50"
                                : "border-gray-200"
                            }`}
                            onClick={() => handleSelectOnwardFare(fare)}
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="font-medium text-lg">
                                  {fare.name || "Standard Fare"}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {fare.refundable || "Partially Refundable*"}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-xl font-bold text-gray-900">
                                  {formatPriceSync(fare.price || 0, currency)}
                                </div>
                                <div className="text-xs text-gray-500">
                                  per adult
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-3 gap-2 mt-3 text-xs text-gray-600">
                              <div className="flex items-center">
                                <FaSuitcase className="mr-1" />
                                <span>{fare.baggage}</span>
                              </div>
                              <div className="flex items-center">
                                <FaExchangeAlt className="mr-1" />
                                <span>
                                  {fare.dateChangeFee || "Contact Airlines"}
                                </span>
                              </div>
                              <div className="flex items-center">
                                {fare.meals === "Available" ? (
                                  <FaUtensils className="mr-1 text-green-500" />
                                ) : (
                                  <FaInfoCircle className="mr-1" />
                                )}
                                <span>
                                  {fare.meals === "Available"
                                    ? "Meals Included"
                                    : "Meals Paid"}
                                </span>
                              </div>
                            </div>

                            {/* Show info only if this fare has SSR options */}
                            {(fare.baggageOptions?.length > 0 ||
                              fare.mealOptions?.length > 0) && (
                              <div className="mt-3 pt-2 border-t border-gray-100">
                                <div className="flex items-start text-xs text-gray-600">
                                  <FaInfoCircle className="mr-1 text-blue-400 mt-0.5 flex-shrink-0" />
                                  <span>
                                    Add-ons are optional. Base baggage:{" "}
                                    {fare.originalFare?.baggage?.checkin?.replace(
                                      "|",
                                      ""
                                    ) || onwardFlight?.Flights?.bagCkin}{" "}
                                    |{" "}
                                    {fare.originalFare?.baggage?.cabin?.replace(
                                      "|",
                                      ""
                                    ) || onwardFlight?.Flights?.bagCbin}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4 text-gray-500">
                          No fare options available
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Onward Flight Details */}
            <AnimatePresence>
              {expandedOnwardDetails && onwardSegments.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-4 border-t pt-4"
                >
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    Onward Flight Details
                  </h4>
                  <FlightDetails segments={onwardSegments} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Return Flight */}
          <div className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="flex items-center space-x-2 mb-1">
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                    Return
                  </span>
                  <span className="text-xs text-gray-500">
                    {formatDate(returnFirstSegment?.depDate)}
                  </span>
                </div>
                <h4 className="font-semibold text-gray-800">
                  {returnAirlineName}
                </h4>
              </div>
              <div className="text-right">
                <span className="text-lg font-bold text-gray-900">
                  {selectedReturnFare
                    ? `₹${selectedReturnFare.price.toLocaleString()}`
                    : "Select fare"}
                </span>
              </div>
            </div>

            {/* Flight Route */}
            <div className="flex items-center justify-between mb-3">
              <div className="text-center">
                <div className="text-lg font-bold">
                  {returnFirstSegment?.depCode || "---"}
                </div>
                <div className="text-sm text-gray-500">
                  {formatTime(returnFirstSegment?.depDate)}
                </div>
              </div>

              <div className="flex-1 px-4">
                <div className="relative">
                  <div className="flex items-center">
                    <div className="flex-1 border-t border-gray-300"></div>
                    <div className="mx-2 relative">
                      <FaPlane className="text-green-500 text-sm" />
                      <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 text-xs text-gray-500 whitespace-nowrap">
                        {returnDuration}
                      </div>
                    </div>
                    <div className="flex-1 border-t border-gray-300"></div>
                  </div>
                  <div className="text-center text-xs text-gray-600 mt-1">
                    {getStopsText(returnFlight)}
                  </div>
                </div>
              </div>

              <div className="text-center">
                <div className="text-lg font-bold">
                  {returnLastSegment?.arrCode || "---"}
                </div>
                <div className="text-sm text-gray-500">
                  {formatTime(returnLastSegment?.arrDate)}
                </div>
              </div>
            </div>

            {/* Selected Fare Info */}
            {selectedReturnFare && (
              <div className="mb-3 p-2 bg-green-50 rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center">
                    <FaCheckCircle className="text-green-500 mr-2" />
                    <span className="font-medium">
                      {selectedReturnFare.name}
                    </span>
                  </div>
                  <span className="text-gray-600">
                    {selectedReturnFare.baggage}
                  </span>
                </div>

                {/* Show SSR info for selected return fare */}
                {selectedReturnFare.ssrInfo && (
                  <div className="mt-2 text-xs text-gray-600">
                    <div className="flex items-start">
                      <FaInfoCircle className="mr-1 text-green-400 mt-0.5" />
                      <div>
                        {selectedReturnFare.ssrInfo.baggageOption && (
                          <div className="mb-1">
                            • Extra Baggage:{" "}
                            {selectedReturnFare.ssrInfo.baggageOption.baggDesc}{" "}
                            ({formatPriceSync(selectedReturnFare.ssrInfo.baggageOption.baggAmt, currency)})
                          </div>
                        )}
                        {selectedReturnFare.ssrInfo.mealOption && (
                          <div>
                            • In-flight Meal:{" "}
                            {selectedReturnFare.ssrInfo.mealOption.mealDesc} (₹
                            {selectedReturnFare.ssrInfo.mealOption.mealAmt})
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* View Prices Button */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center text-sm text-gray-600">
                <FaClock className="mr-1 text-gray-400" />
                <span>{returnDuration}</span>
                <span className="mx-2">•</span>
                <span>{returnFirstSegment?.flightNo || "---"}</span>
                <span className="mx-2">•</span>
                <span className="flex items-center">
                  <FaChair className="mr-1 text-gray-400" />
                  {returnFlight?.Flights?.bagCbin}
                </span>
              </div>

              <button
                onClick={handleViewReturnPrices}
                className="px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                {loadingReturnFares ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Loading...
                  </>
                ) : showReturnFares ? (
                  "Hide Prices"
                ) : (
                  "View Prices"
                )}
              </button>
            </div>

            {/* Return Flight Details Toggle */}
            <div className="border-t pt-3 mt-3">
              <button
                onClick={() => setExpandedReturnDetails(!expandedReturnDetails)}
                className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium w-full justify-end"
              >
                {expandedReturnDetails ? (
                  <>
                    Hide Flight Details <FaChevronUp className="ml-1" />
                  </>
                ) : (
                  <>
                    View Flight Details <FaChevronDown className="ml-1" />
                  </>
                )}
              </button>
            </div>

            {/* Return Fare Options */}
            <AnimatePresence>
              {showReturnFares && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-4 border-t pt-4"
                >
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    Choose Return Fare
                  </h4>
                  {loadingReturnFares ? (
                    <div className="text-center py-4">
                      <FaSpinner className="animate-spin text-green-500 text-xl mx-auto" />
                      <p className="text-gray-500 mt-2">
                        Loading fare options...
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {returnFareOptions.length > 0 ? (
                        returnFareOptions.map((fare, idx) => (
                          <div
                            key={fare.id || idx}
                            className={`border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                              selectedReturnFare?.id === fare.id
                                ? "border-green-500 bg-green-50"
                                : "border-gray-200"
                            }`}
                            onClick={() => handleSelectReturnFare(fare)}
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="font-medium text-lg">
                                  {fare.name || "Standard Fare"}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {fare.refundable || "Partially Refundable*"}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-xl font-bold text-gray-900">
                                  {formatPriceSync(fare.price || 0, currency)}
                                </div>
                                <div className="text-xs text-gray-500">
                                  per adult
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-3 gap-2 mt-3 text-xs text-gray-600">
                              <div className="flex items-center">
                                <FaSuitcase className="mr-1" />
                                <span>{fare.baggage}</span>
                              </div>
                              <div className="flex items-center">
                                <FaExchangeAlt className="mr-1" />
                                <span>
                                  {fare.dateChangeFee || "Contact Airlines"}
                                </span>
                              </div>
                              <div className="flex items-center">
                                {fare.meals === "Available" ? (
                                  <FaUtensils className="mr-1 text-green-500" />
                                ) : (
                                  <FaInfoCircle className="mr-1" />
                                )}
                                <span>
                                  {fare.meals === "Available"
                                    ? "Meals Included"
                                    : "Meals Paid"}
                                </span>
                              </div>
                            </div>

                            {/* Show info only if this fare has SSR options */}
                            {(fare.baggageOptions?.length > 0 ||
                              fare.mealOptions?.length > 0) && (
                              <div className="mt-3 pt-2 border-t border-gray-100">
                                <div className="flex items-start text-xs text-gray-600">
                                  <FaInfoCircle className="mr-1 text-green-400 mt-0.5 flex-shrink-0" />
                                  <span>
                                    Add-ons are optional. Base baggage:{" "}
                                    {fare.originalFare?.baggage?.checkin?.replace(
                                      "|",
                                      ""
                                    ) || returnFlight?.Flights?.bagCkin}{" "}
                                    |{" "}
                                    {fare.originalFare?.baggage?.cabin?.replace(
                                      "|",
                                      ""
                                    ) || returnFlight?.Flights?.bagCbin}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4 text-gray-500">
                          No fare options available
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Return Flight Details */}
            <AnimatePresence>
              {expandedReturnDetails && returnSegments.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-4 border-t pt-4"
                >
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    Return Flight Details
                  </h4>
                  <FlightDetails segments={returnSegments} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Combined Booking Section */}
        {(selectedOnwardFare || selectedReturnFare) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 border-t pt-6"
          >
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Round Trip Summary
                  </h3>
                  <p className="text-gray-600">Review your selected fares</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-700">
                    {formatPriceSync(totalSelectedPrice, currency)}
                  </div>
                  <div className="text-sm text-gray-500">
                    Total for round trip
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* Onward Summary */}
                <div className="bg-white rounded-lg p-4 border">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded mr-2">
                        Onward
                      </span>
                      <span className="font-medium">{onwardAirlineName}</span>
                    </div>
                    <div className="font-bold">
                      ₹
                      {selectedOnwardFare?.price?.toLocaleString() ||
                        baseOnwardPrice.toLocaleString()}
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">
                    {selectedOnwardFare?.name || "Standard Fare"} •{" "}
                    {selectedOnwardFare?.baggage}
                  </div>

                  {/* Show SSR details in onward summary */}
                  {selectedOnwardFare?.ssrInfo && (
                    <div className="mt-2 text-xs text-gray-600">
                      {selectedOnwardFare.ssrInfo.baggageOption && (
                        <div className="mb-1">
                          • Extra Baggage:{" "}
                          {selectedOnwardFare.ssrInfo.baggageOption.baggDesc}{" "}
                          (+{formatPriceSync(selectedOnwardFare.ssrInfo.baggageOption.baggAmt, currency)})
                        </div>
                      )}
                      {selectedOnwardFare.ssrInfo.mealOption && (
                        <div>
                          • In-flight Meal:{" "}
                          {selectedOnwardFare.ssrInfo.mealOption.mealDesc} (+₹
                          {selectedOnwardFare.ssrInfo.mealOption.mealAmt})
                        </div>
                      )}
                    </div>
                  )}

                  <div className="text-xs text-gray-500 mt-1">
                    {onwardFirstSegment?.depCode || "---"} →{" "}
                    {onwardLastSegment?.arrCode || "---"} •{" "}
                    {onwardFirstSegment?.flightNo || "---"}
                  </div>
                </div>

                {/* Return Summary */}
                <div className="bg-white rounded-lg p-4 border">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center">
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded mr-2">
                        Return
                      </span>
                      <span className="font-medium">{returnAirlineName}</span>
                    </div>
                    <div className="font-bold">
                      ₹
                      {selectedReturnFare?.price?.toLocaleString() ||
                        baseReturnPrice.toLocaleString()}
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">
                    {selectedReturnFare?.name || "Standard Fare"} •{" "}
                    {selectedReturnFare?.baggage}
                  </div>

                  {/* Show SSR details in return summary */}
                  {selectedReturnFare?.ssrInfo && (
                    <div className="mt-2 text-xs text-gray-600">
                      {selectedReturnFare.ssrInfo.baggageOption && (
                        <div className="mb-1">
                          • Extra Baggage:{" "}
                          {selectedReturnFare.ssrInfo.baggageOption.baggDesc}{" "}
                          (+{formatPriceSync(selectedReturnFare.ssrInfo.baggageOption.baggAmt, currency)})
                        </div>
                      )}
                      {selectedReturnFare.ssrInfo.mealOption && (
                        <div>
                          • In-flight Meal:{" "}
                          {selectedReturnFare.ssrInfo.mealOption.mealDesc} (+₹
                          {selectedReturnFare.ssrInfo.mealOption.mealAmt})
                        </div>
                      )}
                    </div>
                  )}

                  <div className="text-xs text-gray-500 mt-1">
                    {returnFirstSegment?.depCode || "---"} →{" "}
                    {returnLastSegment?.arrCode || "---"} •{" "}
                    {returnFirstSegment?.flightNo || "---"}
                  </div>
                </div>
              </div>

              {/* Book Now Button */}
              <div className="flex justify-end">
                <button
                  onClick={handleBookNow}
                  disabled={!selectedOnwardFare || !selectedReturnFare}
                  className={`px-8 py-3 rounded-lg font-bold text-lg transition-all duration-300 ${
                    selectedOnwardFare && selectedReturnFare
                      ? "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  BOOK NOW
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default RoundTripFlightCard;
